import React from 'react';
import { t } from 'Src/i18n';
import { Hr } from 'Components/frameworks/hr/Hr';
import { AdditionalTransportFilter } from 'Components/widgets/additional-transports/additional-transports-filters/AdditionalTransportFilter';
import { AdditionalTransportFiltersHeader } from 'Components/widgets/additional-transports/additional-transports-filters/AdditionalTransportFiltersHeader';
import { AdditionalTransportFilterDurationRange } from 'Components/widgets/additional-transports/additional-transports-filters/AdditionalTransportFilterDurationRange';
import { AdditionalTransportAdvancedSearchForm } from 'Components/widgets/additional-transports/additional-transports-search/AdditionalTransportAdvancedSearchForm';
import { AdditionalTransportFilterWithButtons } from 'Components/widgets/additional-transports/additional-transports-filters/AdditionalTransportFilterWithButtons';
import { ArrivalDepartureRangeFilter } from 'Components/widgets/additional-transports/additional-transports-filters/ArrivalDepartureRangeFilter';
import { useFeatureFlip } from 'Src/hooks/use-feature-flip/useFeatureFlip';
type AdditionalTransportFiltersProps = {
  hasNoAlternativeTransport: boolean;
  hasOnlyTrain: boolean;
  proposal: Proposal;
  state: TransportFiltersState;
  updateFilters: (key: TransportFiltersKeys, value: string | TransportDateRange) => void;
  resetFilters: () => void;
};
export const AdditionalTransportFilters = ({
  hasNoAlternativeTransport,
  hasOnlyTrain,
  proposal,
  state,
  updateFilters,
  resetFilters,
}: AdditionalTransportFiltersProps) => {
  const {
    stopovers,
    connectingCities,
    durationRanges,
    companies,
    defaultInboundDepartureTimeRange,
    defaultOutboundDepartureTimeRange,
    inboundDepartureTimeRange,
    outboundDepartureTimeRange,
  } = state;
  const openFilterDefault = !(hasNoAlternativeTransport || hasOnlyTrain);
  const isAdvancedSearchOpened = useFeatureFlip('booking.transport.advancedSearchOpened');
  return (
    <div data-testid="AdditionalTransportFilters" className="px-20 py-30">
      <AdditionalTransportFilter
        isOpen={(hasNoAlternativeTransport && !hasOnlyTrain) || isAdvancedSearchOpened}
        title={t('booking.transport.criteria.advancedSearch')}
      >
        <AdditionalTransportAdvancedSearchForm proposal={proposal as ProposalWithTransport} />
      </AdditionalTransportFilter>
      <AdditionalTransportFiltersHeader state={state} resetFilters={resetFilters} />
      <Hr className="my-20" />
      <AdditionalTransportFilter
        isOpen={openFilterDefault}
        title={t('filters.layovers.categoryTitle')}
      >
        <AdditionalTransportFilterWithButtons
          type="stopovers"
          filters={stopovers.filter((stopover) => stopover.flightIdList.length > 0)}
          updateFilters={updateFilters}
          withLength
        />
      </AdditionalTransportFilter>
      <AdditionalTransportFilter
        isOpen={openFilterDefault}
        title={t('filters.companiesCategoryTitle')}
      >
        <AdditionalTransportFilterWithButtons
          type="companies"
          filters={companies}
          updateFilters={updateFilters}
          withLength
        />
      </AdditionalTransportFilter>
      {connectingCities.length > 0 && (
        <AdditionalTransportFilter
          isOpen={openFilterDefault}
          title={t('filters.layovers.stop.connectionCities')}
        >
          <AdditionalTransportFilterWithButtons
            type="connectingCities"
            filters={connectingCities}
            updateFilters={updateFilters}
            withLength
          />
        </AdditionalTransportFilter>
      )}
      <AdditionalTransportFilter
        isOpen={openFilterDefault}
        title={t('filters.departureArrival.times')}
      >
        {defaultInboundDepartureTimeRange && (
          <div className="mb-20">
            <span className="text-16 font-semibold">{t('booking.transport.inbound')}</span>
            <ArrivalDepartureRangeFilter
              startTime={defaultInboundDepartureTimeRange.start}
              endTime={defaultInboundDepartureTimeRange.end}
              setRange={(value) => updateFilters('inboundDepartureTimeRange', value)}
              selectedRange={inboundDepartureTimeRange}
            />
          </div>
        )}
        {defaultOutboundDepartureTimeRange && (
          <div>
            <span className="text-16 font-semibold">{t('booking.transport.outbound')}</span>
            <ArrivalDepartureRangeFilter
              startTime={defaultOutboundDepartureTimeRange.start}
              endTime={defaultOutboundDepartureTimeRange.end}
              setRange={(value) => updateFilters('outboundDepartureTimeRange', value)}
              selectedRange={outboundDepartureTimeRange}
            />
          </div>
        )}
      </AdditionalTransportFilter>
      <AdditionalTransportFilter
        isOpen={openFilterDefault}
        title={t('filters.flightDurationTitle')}
      >
        <AdditionalTransportFilterDurationRange
          durationRanges={durationRanges.filter((range) => range.flightIdList.length > 0)}
          updateFilters={updateFilters}
        />
      </AdditionalTransportFilter>
    </div>
  );
};
